import { v4 as uuidv4 } from 'uuid';
import { z } from 'zod';
import { apiClient, getApiOrigin } from '../../../../shared/api-client';
import { getDurableItem, getDurableKeys, getPersistentJson, removeDurableItem, setDurableItem } from '../../../../vrs-auth/storage';
import { checkedContact } from './contactDetailScreenUtils';
const id = z.string().min(1).max(120);
const optionalText = z.string().max(1000).nullable().optional();
const manualSchema = z.object({
    linkedClientId: id.refine(value => value.trim().length > 0 && !/<[^>]*>/.test(value), 'Choose a valid directory contact.').optional(),
    name: z.string().trim().min(1, 'Add a name, phone number, or email.').max(100).refine(value => value.replace(/<[^>]*>/g, '').replace(/&[#\w]+;/g, '').replace(/javascript:/gi, '').replace(/on\w+\s*=/gi, '').trim().length > 0, 'Contact name is required.'),
    phoneNumber: z.string().regex(/^\+?\d{7,16}$/, 'Enter a valid phone number, including its country code.').nullable().optional(),
    email: z.string().email('Enter a valid email address.').max(254).nullable().optional(),
    organization: optionalText, notes: optionalText,
    avatarColor: z.string().regex(/^#[0-9a-fA-F]{6}$/).optional(),
    isFavorite: z.boolean().optional(), groupIds: z.array(id).max(50).optional()
}).strict();
const inputSchema = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('manual'), contact: manualSchema }).strict(),
    z.object({ kind: z.literal('request'), request: z.object({ recipientClientId: id, message: optionalText }).strict() }).strict()
]);
const pendingSchema = z.object({
    version: z.literal(1), clientId: id, origin: z.string().url(), operationId: z.string().uuid(),
    input: inputSchema, query: z.string().max(1000).optional()
}).strict();
const contactSchema = z.object({
    linkedClientId: id.refine(value => value.trim().length > 0 && !/<[^>]*>/.test(value), 'Choose a valid directory contact.').optional(),
    id, name: z.string().min(1), email: z.string().nullable(), phoneNumber: z.string().nullable(),
    organization: z.string().nullable(), notes: z.string().nullable(), avatarColor: z.string().nullable(),
    isFavorite: z.boolean(), groupIds: z.array(id), groupNames: z.array(z.string())
});
const requestSchema = z.object({
    id, requesterClientId: id, recipientClientId: id,
    status: z.enum(['pending', 'accepted', 'rejected', 'cancelled']), message: z.string().nullable(),
    createdAt: z.string().datetime(), respondedAt: z.string().datetime().nullable()
});
const resultSchema = z.discriminatedUnion('kind', [
    z.object({ operationId: z.string().uuid(), clientId: id, kind: z.literal('manual'), entityId: id, contact: contactSchema.nullable() }),
    z.object({ operationId: z.string().uuid(), clientId: id, kind: z.literal('request'), entityId: id, request: requestSchema.nullable() })
]);
const running = new Set();
export function currentContactCreationScope() {
    const user = z.object({ id: id.optional(), userId: id.optional() }).safeParse(getPersistentJson('vrs_user_info'));
    const clientId = user.success ? user.data.id || user.data.userId : undefined;
    const origin = getApiOrigin();
    if (!clientId || !origin)
        throw new Error('Session expired. Sign in again.');
    return { clientId, origin };
}
function contactCreationStoragePrefix(scope) {
    return `vrs_pending_contact_creation:${encodeURIComponent(scope.origin)}:${encodeURIComponent(scope.clientId)}:`;
}
export function contactCreationStorageKey(scope, operationId) {
    return `${contactCreationStoragePrefix(scope)}${operationId}`;
}
export function isCurrentContactCreationScope(scope) {
    try {
        const current = currentContactCreationScope();
        return current.clientId === scope.clientId && current.origin === scope.origin;
    }
    catch {
        return false;
    }
}
function requireCurrentScope(scope) {
    if (!isCurrentContactCreationScope(scope))
        throw new Error('Your account changed. Reopen Contacts.');
}
export async function readPendingContactCreation(scope) {
    let keys;
    const prefix = contactCreationStoragePrefix(scope);
    try {
        keys = await getDurableKeys(prefix);
    }
    catch {
        throw new Error('The earlier save could not be checked on this device. Try again.');
    }
    if (!keys.length)
        return null;
    // The identity is in the durable key, so a damaged JSON draft can still be
    // resolved by the server. Resolve one saved attempt at a time if several exist.
    const storageKey = keys.sort()[0];
    const operation = z.string().uuid().safeParse(storageKey.slice(prefix.length));
    if (!operation.success)
        throw new Error('The earlier save is unreadable on this device. Contact support.');
    const identity = { ...scope, operationId: operation.data, storageKey };
    let raw;
    try {
        raw = await getDurableItem(storageKey);
    }
    catch {
        throw new Error('The earlier save could not be read on this device. Try again.');
    }
    try {
        const pending = pendingSchema.parse(JSON.parse(raw || 'null'));
        if (pending.clientId !== scope.clientId || pending.origin !== scope.origin || pending.operationId !== operation.data)
            throw new Error('Scope mismatch');
        return { ...identity, input: pending.input, query: pending.query };
    }
    catch {
        return { ...identity, input: null };
    }
}
async function withContactDeadline(run) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 30000);
    try {
        return await run(controller.signal);
    }
    finally {
        clearTimeout(timer);
    }
}
async function prepareInput(scope, draft) {
    if (draft.kind === 'manual') {
        const checked = manualSchema.safeParse(draft.contact);
        if (!checked.success)
            throw new Error(checked.error.issues[0]?.message || 'Check the contact details.');
        return { kind: 'manual', contact: checked.data };
    }
    if (draft.kind === 'participantRequest') {
        // A call-history row already supplies the recipient identity. The same
        // server receipt and participant rules still authorize the request.
        const recipient = z.string().uuid().safeParse(draft.recipientClientId);
        if (!recipient.success || recipient.data.trim() !== recipient.data || recipient.data === scope.clientId) {
            throw new Error('Refresh the call participants.');
        }
        return { kind: 'request', request: { recipientClientId: recipient.data, message: null } };
    }
    const query = draft.query.trim(), message = draft.message.trim();
    if (query.length < 2 || query.length > 1000)
        throw new Error('Enter a public handle or VRS number.');
    if (message.length > 1000)
        throw new Error('Keep the request message within 1,000 characters.');
    requireCurrentScope(scope);
    const response = await withContactDeadline(signal => apiClient.get(`/api/contacts/lookup?query=${encodeURIComponent(query)}`, { retryAuth: false, signal }));
    if (response.error)
        throw new Error(response.error);
    requireCurrentScope(scope);
    const checked = z.object({ result: z.object({ id }) }).safeParse(response.data);
    if (!checked.success)
        throw new Error('The public contact could not be confirmed. Try again.');
    if (draft.expectedRecipientClientId !== undefined && checked.data.result.id !== draft.expectedRecipientClientId) {
        throw new Error('The public contact changed. Look them up again.');
    }
    if (checked.data.result.id === scope.clientId)
        throw new Error('Choose someone other than yourself.');
    return { kind: 'request', request: { recipientClientId: checked.data.result.id, message: message || null } };
}
function checkedResult(value, pending, checkInput = true) {
    const parsed = z.object({ creation: resultSchema }).safeParse(value);
    if (!parsed.success)
        throw new Error('Save not confirmed. Try again.');
    const result = parsed.data.creation;
    if (result.operationId !== pending.operationId || result.clientId !== pending.clientId || (checkInput && result.kind !== pending.input?.kind)
        || (result.kind === 'manual' && result.contact && (result.contact.id !== result.entityId
            || (checkInput && pending.input?.kind === 'manual' && pending.input.contact.linkedClientId
                && result.contact.linkedClientId !== pending.input.contact.linkedClientId)))
        || (result.kind === 'request' && result.request && (result.request.id !== result.entityId
            || result.request.requesterClientId !== pending.clientId
            || (checkInput && (pending.input?.kind !== 'request' || result.request.recipientClientId !== pending.input.request.recipientClientId))))) {
        throw new Error('The save confirmation did not match. Try again.');
    }
    // Confirm that the downstream cache mapper accepts the record before
    // clearing its retry identity; malformed success must remain recoverable.
    if (result.kind === 'manual' && result.contact)
        checkedContact(result.contact, result.entityId);
    return result;
}
/** Existing directory links keep their private data; do not silently discard a different earlier draft. */
function needsPrivateDetailReview(pending, creation) {
    if (pending.input?.kind !== 'manual' || !pending.input.contact.linkedClientId || creation.kind !== 'manual' || !creation.contact)
        return false;
    const draft = pending.input.contact, saved = creation.contact;
    if (draft.linkedClientId !== saved.linkedClientId)
        return true;
    const text = (value) => (value || '').trim();
    return (draft.organization !== undefined && text(draft.organization) !== text(saved.organization))
        || (draft.notes !== undefined && text(draft.notes) !== text(saved.notes))
        || (draft.isFavorite !== undefined && draft.isFavorite !== saved.isFavorite)
        || (draft.groupIds !== undefined && JSON.stringify([...new Set(draft.groupIds)].sort()) !== JSON.stringify([...new Set(saved.groupIds)].sort()));
}
/** Save the retry identity before the request. Never discard an uncertain outcome. */
export async function submitContactCreation(scope, draft, onPending, expectedOperationId, canClearRecovery = () => true) {
    const key = contactCreationStoragePrefix(scope);
    if (running.has(key))
        throw new Error('A contact save is already in progress.');
    running.add(key);
    try {
        requireCurrentScope(scope);
        let pending = await readPendingContactCreation(scope);
        requireCurrentScope(scope);
        if (pending && pending.operationId !== expectedOperationId) {
            onPending(pending);
            throw new Error('An earlier save was found.');
        }
        if (!pending && expectedOperationId)
            throw new Error('The earlier save changed. Reopen Add Contact.');
        const expectedRecipient = draft.kind === 'participantRequest' ? draft.recipientClientId
            : draft.kind === 'request' ? draft.expectedRecipientClientId : undefined;
        if (pending && expectedRecipient !== undefined
            && (pending.input?.kind !== 'request' || pending.input.request.recipientClientId !== expectedRecipient)) {
            onPending(pending);
            throw new Error('The earlier save belongs to a different recipient.');
        }
        if (!pending) {
            const input = await prepareInput(scope, draft);
            requireCurrentScope(scope);
            const saved = { version: 1, ...scope, operationId: uuidv4(), input, ...(draft.kind === 'request' ? { query: draft.query.trim() }
                    : draft.kind === 'participantRequest' ? { query: draft.displayName.trim().slice(0, 1000) } : {}) };
            pending = { ...saved, storageKey: contactCreationStorageKey(scope, saved.operationId) };
            try {
                await setDurableItem(pending.storageKey, JSON.stringify(saved));
            }
            catch {
                throw new Error('Device storage unavailable. Try again.');
            }
        }
        requireCurrentScope(scope);
        onPending(pending);
        if (!pending.input)
            throw new Error('The original details could not be restored.');
        const attempt = pending;
        const response = await withContactDeadline(signal => apiClient.put(`/api/contacts/creations/${encodeURIComponent(attempt.operationId)}?expectedClientId=${encodeURIComponent(scope.clientId)}`, attempt.input, { retryAuth: false, signal }));
        requireCurrentScope(scope);
        if (response.error)
            throw new Error(response.error);
        const creation = checkedResult(response.data, pending);
        if (needsPrivateDetailReview(pending, creation))
            return { creation, cleanupPending: false, reviewRequired: true };
        // A confirmed outcome that the originating view cannot present remains resumable.
        if (!canClearRecovery())
            return { creation, cleanupPending: true };
        try {
            await removeDurableItem(pending.storageKey);
        }
        catch {
            return { creation, cleanupPending: true };
        }
        return { creation, cleanupPending: false };
    }
    finally {
        running.delete(key);
    }
}
/** Compare the validated snapshot, independent of group ordering, before clearing a reviewed draft. */
function contactReviewKey(value) {
    const checked = contactSchema.safeParse(value);
    if (!checked.success)
        return null;
    return JSON.stringify({ ...checked.data, linkedClientId: checked.data.linkedClientId ?? null,
        groupIds: [...checked.data.groupIds].sort(), groupNames: [...checked.data.groupNames].sort() });
}
/** A resolution confirms a completed save or permanently stops an unfinished one. */
export async function resolvePendingContactCreation(scope, expected, acceptedDetails = null, canClearRecovery = () => true) {
    const acceptedKey = acceptedDetails ? contactReviewKey(acceptedDetails) : null;
    if (acceptedDetails && !acceptedKey)
        throw new Error('Check the saved contact details again.');
    const key = contactCreationStoragePrefix(scope);
    if (running.has(key))
        throw new Error('A contact save is already in progress.');
    running.add(key);
    try {
        requireCurrentScope(scope);
        // The immutable key names this exact attempt. Resolution creates no
        // entity, so it remains safe if its payload became unreadable or another
        // view already removed the local record after confirming the outcome.
        if (!z.string().uuid().safeParse(expected.operationId).success || expected.clientId !== scope.clientId
            || expected.origin !== scope.origin || expected.storageKey !== contactCreationStorageKey(scope, expected.operationId)) {
            throw new Error('The earlier save identity did not match this account. Reopen Add Contact.');
        }
        const pending = expected;
        const response = await withContactDeadline(signal => apiClient.post(`/api/contacts/creations/${encodeURIComponent(pending.operationId)}/resolve?expectedClientId=${encodeURIComponent(scope.clientId)}`, {}, { retryAuth: false, signal }));
        requireCurrentScope(scope);
        if (response.error)
            throw new Error(response.error);
        const parsed = z.object({ resolution: z.discriminatedUnion('state', [
                z.object({ operationId: z.string().uuid(), clientId: id, state: z.literal('abandoned') }).strict(),
                z.object({ operationId: z.string().uuid(), clientId: id, state: z.literal('completed'), creation: resultSchema }).strict()
            ]) }).safeParse(response.data);
        if (!parsed.success || parsed.data.resolution.operationId !== pending.operationId || parsed.data.resolution.clientId !== scope.clientId) {
            throw new Error('The earlier save outcome could not be confirmed. Try again.');
        }
        const resolution = parsed.data.resolution;
        if (resolution.state === 'completed') {
            checkedResult({ creation: resolution.creation }, pending, false);
            if (acceptedKey && resolution.creation.kind === 'manual' && resolution.creation.contact
                && contactReviewKey(resolution.creation.contact) !== acceptedKey) {
                // Give every view the newly checked record, but retain the
                // immutable draft until that exact snapshot is accepted.
                return { resolution, cleanupPending: false, reviewRequired: true };
            }
            if (!acceptedKey && needsPrivateDetailReview(pending, resolution.creation))
                return { resolution, cleanupPending: false, reviewRequired: true };
        }
        if (acceptedKey && (resolution.state !== 'completed' || resolution.creation.kind !== 'manual' || !resolution.creation.contact)) {
            throw new Error('The saved contact changed after your review. Check again.');
        }
        if (!canClearRecovery())
            return { resolution, cleanupPending: true };
        try {
            await removeDurableItem(pending.storageKey);
        }
        catch {
            return { resolution, cleanupPending: true };
        }
        return { resolution, cleanupPending: false };
    }
    finally {
        running.delete(key);
    }
}
